import React, { useLayoutEffect, useRef, useEffect } from "react";
import Typography from "@mui/material/Typography";
import SendIcon from "@mui/icons-material/Send";
import { IconButton, Stack, Tooltip } from "@mui/material";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import InputBase from "@mui/material/InputBase";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Grow from "@mui/material/Grow";
import { v4 as uuidv4 } from "uuid";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import AnswerDetailsDialog from "./AnswerDetailsDialog.js";
import MyChart from "./MyChart.js";
import Answering from "./Answering.js";
import QueryResultsDisplay from "./QueryResultsDisplay";
import MarkdownRenderer from "./MarkdownRenderer.js";

const Chat = ({ userName = "" }) => {
  function getCookie(name) {
    return document.cookie
      .split("; ")
      .find(row => row.startsWith(name + "="))
      ?.split("=")[1];
  }
  const [totalAnswers, setTotalAnswers] = React.useState(0);
  const [enabled, setEnabled] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [controlAnswers, setControlAnswers] = React.useState([]);
  const [answers, setAnswers] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [sessionId] = React.useState(uuidv4());
  const [errorMessage, setErrorMessage] = React.useState("");
  const [height, setHeight] = React.useState(480);
  const [openAnswerDetails, setOpenAnswerDetails] = React.useState(false);
  const [size, setSize] = React.useState([0, 0]);
  const [selectedAB] = React.useState(0);

  function reviveApexFormatters(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      const val = obj[key];
      if (key === 'formatter' && typeof val === 'string') {
        try {
          obj[key] = eval('(' + val + ')');
        } catch (e) {
        }
      } else if (typeof val === 'object' && val !== null) {
        reviveApexFormatters(val);
      }
    }
    return obj;
  }

  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [answers]);

  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
      const myh = window.innerHeight - 220;
      if (myh < 346) {
        setHeight(346);
      } else {
        setHeight(myh);
      }
    }
    window.addEventListener("resize", updateSize);
    updateSize();
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const effectRan = React.useRef(false);
  useEffect(() => {
    if (!effectRan.current) {
      const fetchData = async () => {
      };
      fetchData()
        .catch(console.error);
    }
    return () => (effectRan.current = true);
  }, []);

  const handleQuery = (event) => {
    if (event.target.value.length > 0 && loading === false && query !== "")
      setEnabled(true);
    else setEnabled(false);
    setQuery(event.target.value.replace("\n", ""));
  };

  const handleKeyPress = (event) => {
    if (event.code === "Enter" && loading === false && query !== "") {
      getAnswer(query)
    }
  };

  const handleClick = async (e) => {
    e.preventDefault();
    if (query !== "") {
      getAnswer(query);
    }
  };

  useEffect(() => {
    if (answers.length > 0) {
      const lastIdx = answers.length - 1;
      const last = answers[lastIdx];
      if (last && !last.isStreaming && (!controlAnswers[lastIdx] || !controlAnswers[lastIdx].current_tab_view)) {
        setControlAnswers((prev) => {
          const ctrl = [...prev];
          ctrl[lastIdx] = { ...ctrl[lastIdx], current_tab_view: "answer" };
          return ctrl;
        });
      }
    }
  }, [answers, controlAnswers]);

  const getAnswer = async (my_query) => {
    if (!loading && my_query !== "") {
      setControlAnswers((prevState) => [...prevState, {}, {}]);
      setAnswers((prevState) => [
        ...prevState,
        { query: my_query, isUser: true },
        { isStreaming: true }
      ]);
      setEnabled(false);
      setLoading(true);
      setErrorMessage("");
      setQuery("");

      try {
        const csrfToken = getCookie("csrf_token");
        const queryUuid = uuidv4();
        const response = await fetch("https://api.datasenseai-neuronize.charlizeonline.net/api/agente/question", {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ question: my_query, queryUuid, sessionId }),
        });

        if (!response.ok) throw new Error("Error al consultar el agente");
        const data = await response.json();
        setAnswers((prevState) => {
          const newState = [...prevState];
          for (let i = newState.length - 1; i >= 0; i--) {
            if (newState[i].isStreaming) {
              newState[i] = {
                ...newState[i],
                text: data.completion,
                chart: data.chart || null,
                auditId: data.audit_id || null,
                isStreaming: false
              };
              break;
            }
          }
          return newState;
        });
        setLoading(false);
        setEnabled(false);
        setTotalAnswers((prevState) => prevState + 1);
      } catch (error) {
        console.error("[Chat] Error en getAnswer:", error);
        setErrorMessage(error.toString());
        setLoading(false);
        setEnabled(false);
        setAnswers((prevState) => {
          const newState = [...prevState];
          for (let i = newState.length - 1; i >= 0; i--) {
            if (newState[i].isStreaming) {
              newState[i] = {
                ...newState[i],
                text: "Error occurred while getting response",
                isStreaming: false,
                error: true,
              };
              break;
            }
          }
          return newState;
        });
      }
    }
  };

  const handleEvaluation = async (index, value) => {
    const answer = answers[index];
    if (!answer?.auditId) return;

    const newValue = controlAnswers[index].evaluation === value ? null : value;

    setControlAnswers((prev) => {
      const newState = [...prev];
      newState[index] = {
        ...newState[index],
        evaluation: newValue
      };
      return newState;
    });

    const csrfToken = getCookie("csrf_token");
    try {
      await fetch(`https://api.datasenseai-neuronize.charlizeonline.net/api/audit/evaluation/${answer.auditId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ evaluation: newValue }),
      });
    } catch (error) {
      console.error("Error al actualizar evaluación:", error);
    }

  };

  const handleGenerateChart = async (answer, index) => {
    if (!answer.chart) {
      setAnswers((prevState) => {
        const newState = [...prevState];
        newState[index].chart = "loading";
        return newState;
      });
      try {
        const csrfToken = getCookie("csrf_token");
        const response = await fetch(
          `https://api.datasenseai-neuronize.charlizeonline.net/api/agente/chart/${answer.auditId}`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify({
              data: answer.text,
            }),
          }
        );

        if (!response.ok) throw new Error("Error al generar gráfico");
        const chartResult = await response.json();
        setAnswers((prevState) => {
          const newState = [...prevState];
          newState[index].chart = chartResult;
          return newState;
        });
      } catch (error) {
        setAnswers((prevState) => {
          const newState = [...prevState];
          newState[index].chart = { error: "No se pudo generar el gráfico" };
          return newState;
        });
      }
    }
  };

  const handleCloseAnswerDetails = () => {
    setOpenAnswerDetails(false);
  };

  const handleShowTab = (index, type) => () => {
    const updatedItems = [...controlAnswers];
    updatedItems[index] = { ...updatedItems[index], current_tab_view: type };
    setControlAnswers(updatedItems);
  };

  return (
    <Box sx={{ pl: 2, pr: 2, pt: 0, pb: 0 }}>
      {errorMessage !== "" && (
        <Alert
          severity="error"
          sx={{
            position: "fixed",
            width: "80%",
            top: "65px",
            left: "20%",
            marginLeft: "-10%",
          }}
          onClose={() => {
            setErrorMessage("");
          }}
        >
          {errorMessage}
        </Alert>
      )}

      <Box
        id="chatHelper"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: { xs: "70vh", sm: height },
          overflow: "hidden",
          overflowY: "auto",
          px: { xs: 0.5, sm: 2 },
          fontSize: { xs: "0.775rem", sm: "0.9rem", md: "1.1rem" },
          "& .MuiTypography-root": {
            fontSize: { xs: "0.775rem", sm: "0.9rem", md: "1.1rem" },
          },
        }}
      >
        {answers.length > 0 ? (
          <ul style={{ paddingBottom: 14, margin: 0, listStyleType: "none" }}>
            {answers.map((answer, index) => {
              if (!answer) return null;
              return (
                <li key={"meg" + index} style={{ marginBottom: 0 }}>
                  {answer && answer.hasOwnProperty("text") ? (
                    <Box
                      sx={{
                        borderRadius: 8,
                        pl: 1,
                        pr: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: 1,
                      }}
                    >
                      <Box sx={{ pr: 1, pt: 1.5, pl: 0.5 }}>
                        <img
                          src="/images/logo_secundario.png"
                          height={20}
                        />
                      </Box>
                      <Box sx={{ p: 0, flex: 1 }}>
                        <Box>
                          <Grow
                            in={controlAnswers[index]?.current_tab_view === "answer"}
                            timeout={{ enter: 600, exit: 0 }}
                            style={{ transformOrigin: "50% 0 0" }}
                            mountOnEnter
                            unmountOnExit
                          >
                            <Box
                              id={"answer" + index}
                              sx={{
                                opacity: 0.8,
                                "&.MuiBox-root": {
                                  animation: "fadeIn 0.8s ease-in-out forwards",
                                },
                                mt: 1,
                              }}
                            >
                              <Typography component="div" variant="body1" sx={{ fontSize: { xs: "0.775rem", sm: "0.9rem", md: "1.1rem" } }}>
                                <MarkdownRenderer content={answer.text} />
                              </Typography>
                            </Box>
                          </Grow>

                          {answer && answer.hasOwnProperty("queryResults") && (
                            <Grow
                              in={
                                controlAnswers[index].current_tab_view ===
                                "records"
                              }
                              timeout={{ enter: 600, exit: 0 }}
                              style={{ transformOrigin: "50% 0 0" }}
                              mountOnEnter
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  opacity: 0.8,
                                  "&.MuiBox-root": {
                                    animation: "fadeIn 0.8s ease-in-out forwards",
                                  },
                                  transform: "translateY(10px)",
                                  "&.MuiBox-root-appear": {
                                    transform: "translateY(0)",
                                  },
                                  mt: 1,
                                }}
                              >
                                <QueryResultsDisplay
                                  index={index}
                                  answer={answer}
                                />
                              </Box>
                            </Grow>
                          )}

                          {answer && answer.hasOwnProperty("chart") && answer.chart && answer.chart.hasOwnProperty("chart_type") && (
                            <Grow
                              in={
                                controlAnswers[index].current_tab_view ===
                                "chart"
                              }
                              timeout={{ enter: 600, exit: 0 }}
                              style={{ transformOrigin: "50% 0 0" }}
                              mountOnEnter
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  opacity: 0.8,
                                  "&.MuiBox-root": {
                                    animation:
                                      "fadeIn 0.9s ease-in-out forwards",
                                  },
                                  transform: "translateY(10px)",
                                  "&.MuiBox-root-appear": {
                                    transform: "translateY(0)",
                                  },
                                  mt: 1,
                                }}
                              >
                                <MyChart
                                  caption={answer.chart.caption}
                                  options={reviveApexFormatters(answer.chart.chart_configuration.options)}
                                  series={
                                    answer.chart.chart_configuration.series
                                  }
                                  type={answer.chart.chart_type}
                                />
                              </Box>
                            </Grow>
                          )}
                        </Box>

                        {answer && answer.hasOwnProperty("text") && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: { xs: 0.5, sm: 1, md: 1.5 },
                              py: { xs: 0.5, sm: 1 },
                              mt: { xs: 0.5, sm: 1 },
                            }}
                          >
                            <Box display="flex" gap={1}>
                              <Button
                                sx={(theme) => ({
                                  pr: { xs: 0.5, sm: 1 },
                                  pl: { xs: 0.5, sm: 1 },
                                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "1rem" },
                                  "& .MuiButton-startIcon": {
                                    fontSize: { xs: 16, sm: 18, md: 20 },
                                  },
                                  "&.Mui-disabled": {
                                    borderBottom: 0.5,
                                    color: theme.palette.primary.main,
                                    borderRadius: 0,
                                  },
                                })}
                                size="small"
                                color="secondaryText"
                                disabled={controlAnswers[index].current_tab_view === "answer"}
                                onClick={handleShowTab(index, "answer")}
                                startIcon={<QuestionAnswerOutlinedIcon />}
                              >
                                Answer
                              </Button>

                              <Button
                                sx={(theme) => ({
                                  pr: { xs: 0.5, sm: 1 },
                                  pl: { xs: 0.5, sm: 1 },
                                  fontSize: { xs: "0.7rem", sm: "0.8rem", md: "1rem" },
                                  "& .MuiButton-startIcon": {
                                    fontSize: { xs: 16, sm: 18, md: 20 },
                                  },
                                  "&.Mui-disabled": {
                                    borderBottom: 0.5,
                                    color: theme.palette.primary.main,
                                    borderRadius: 0,
                                  },
                                })}
                                size="small"
                                color="secondaryText"
                                disabled={controlAnswers[index].current_tab_view === "chart"}
                                onClick={async () => {
                                  handleShowTab(index, "chart")();
                                  await handleGenerateChart(answer, index);
                                }}
                                startIcon={<InsightsOutlinedIcon />}
                              >
                                Chart
                              </Button>
                            </Box>

                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography sx={{ fontWeight: 500 }}>Was this response helpful?</Typography>

                              <Stack direction="row" spacing={0.5}>
                                {[
                                  { icon: <SentimentVerySatisfiedIcon />, value: "Positiva", color: "success.main", label: "Yes" },
                                  { icon: <SentimentVeryDissatisfiedIcon />, value: "Negativa", color: "error.main", label: "No" },
                                ].map((opt) => (
                                  <Tooltip title={opt.label} key={opt.value}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEvaluation(index, opt.value)}
                                      sx={(theme) => ({
                                        fontSize: { xs: "1.2rem", sm: "1.5rem", md: "1.8rem" },
                                        color:
                                          controlAnswers[index]?.evaluation === opt.value
                                            ? theme.palette[opt.color.split(".")[0]].main
                                            : theme.palette.text.secondary,
                                        "&:hover": {
                                          color: theme.palette[opt.color.split(".")[0]].main,
                                        },
                                      })}
                                    >
                                      {opt.icon}
                                    </IconButton>
                                  </Tooltip>
                                ))}
                              </Stack>
                            </Box>
                          </Box>
                        )}
                        {answer.chart === "loading" && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              ml: 1,
                            }}
                          >
                            <CircularProgress size={16} color="primary" />
                            <Typography
                              variant="caption"
                              color="secondaryText"
                              sx={{ ml: 1 }}
                            >
                              Generating chart...
                            </Typography>
                          </Box>
                        )}

                        {answer.chart && answer.chart.hasOwnProperty("rationale") && (
                          <Typography variant="caption" color="secondaryText" sx={{ fontSize: 13.5, fontWeight: 'bold' }}>
                            {answer.chart.rationale}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : answer && answer.isUser ? (
                    <Grid container justifyContent="flex-end">
                      <Box
                        sx={(theme) => ({
                          textAlign: "right",
                          borderRadius: 8,
                          fontWeight: 500,
                          pt: 1,
                          pb: 1,
                          pl: 2,
                          pr: 2,
                          mt: 2,
                          mb: 1.5,
                          mr: 1,
                          boxShadow: "rgba(0, 0, 0, 0.05) 0px 4px 12px",
                          background: "#01579B",
                          color: "#fff",
                        })}
                      >
                        <Typography variant="body1" sx={{ color: '#fff' }}>{answer.query}</Typography>
                      </Box>
                    </Grid>
                  ) : null}
                </li>
              );
            })}

            {loading && (
              <Box sx={{ p: 0, pl: 1, mb: 2, mt: 1 }}>
                <Answering loading={loading} />
              </Box>
            )}
            <li ref={scrollRef} />
          </ul>
        ) : (
          <Box
            textAlign={"center"}
            sx={{
              pl: 1,
              pt: 1,
              pr: 1,
              pb: 6,
              height: height,
              display: "flex",
              alignItems: "flex-end",
            }}
          >
            <div style={{ width: "100%" }}>
              <img
                src="/images/logo_secundario.png"
                alt="Imagen"
                style={{ width: "150px", height: "150px" }}
              />
              <Typography variant="h5" sx={{ pb: 1, fontWeight: 500 }}>
                Neuronize - Analyst Agent
              </Typography>
              <Typography sx={{ pb: 4, fontWeight: 400 }}>
                I am an assistant specialized in data analysis and operations optimization for Neuronize. My primary objective is to analyze operational performance, maintenance times, bearing lubrication efficiency, and production cycles, using historical data. I identify trends, bottlenecks, and improvement opportunities, generating actionable insights to optimize railway operations management and preventive maintenance.
              </Typography>
              <Typography
                color="primary"
                sx={{ fontSize: "1.1rem", pb: 1, fontWeight: 500 }}
              >
              </Typography>
            </div>
          </Box>
        )}
      </Box>

      <Paper
        component="form"
        sx={(theme) => ({
          zIndex: 0,
          p: 1,
          mb: 2,
          display: "flex",
          alignItems: "center",
          boxShadow:
            "rgba(17, 17, 26, 0.05) 0px 4px 16px, rgba(17, 17, 26, 0.05) 0px 8px 24px, rgba(17, 17, 26, 0.05) 0px 16px 56px",
          border: 1,
          borderColor: "divider",
          borderRadius: 6,
          flexDirection: { xs: "row", sm: "row" },
          gap: 1,
          fontSize: { xs: 12, sm: 14 },
          minHeight: 48,
        })}
      >
        <Box sx={{ pl: 0.5, pr: 0.5, display: "flex", alignItems: "center", height: 32 }}>
          <img
            src="/images/logo_secundario.png"
            alt="Imagen"
            height={20}
            style={{ display: "block" }}
          />
        </Box>
        <InputBase
          required
          id="query"
          name="query"
          placeholder="Type your question here..."
          fullWidth
          multiline
          minRows={1}
          maxRows={5}
          onChange={handleQuery}
          onKeyDown={handleKeyPress}
          value={query}
          variant="outlined"
          inputProps={{ maxLength: 1000 }}
          sx={{ pl: 1, pr: 1, fontSize: { xs: "0.775rem", sm: "0.9rem", md: "1.1rem" }, minHeight: 32, alignItems: "center", display: "flex" }}
        />
        <Divider sx={{ height: 32, mx: 0.5 }} orientation="vertical" />
        <IconButton
          sx={{ p: 1, display: "flex", alignItems: "center", height: 32 }}
          aria-label="directions"
          disabled={!enabled}
          onClick={handleClick}
        >
          <SendIcon sx={{ color: '#044271', fontSize: 22 }} />
        </IconButton>
      </Paper>

      {selectedAB > 0 && (
        <AnswerDetailsDialog
          open={openAnswerDetails}
          handleClose={handleCloseAnswerDetails}
          answer={answers[selectedAB]}
          question={
            answers[selectedAB - (answers[selectedAB].countRationals + 1)].query
          }
        />
      )}
    </Box>
  );
};

export default Chat;
